import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import {
    ProductFieldNames,
    FundProductInterface,
    FundProductTypesEnum,
    IEarlyRedemption,
    ProductRedemptionFieldNames,
    ICouponPayment,
    ProductCouponFieldNames,
    IUnderlyingInstrument,
    ProductInstrumentFieldNames,
    ProductSubType,
    FundProductStatusesEnum,
    ProductGroup,
    ProductCategory,
    FundamentalCompanyService,
    IFundamentalCompany,
    HttpStatusCodesEnum
} from '@spa/api';
import { ReportExtensions } from '@spa/reporting';
import { AmountControl, BaseControl, Helper } from 'appShared/classes';
import { Subject, Subscription, of } from 'rxjs';
import { map } from 'rxjs/operators';

import {
    CouponPaymentFormControls,
    DurationInMonthsMaxLength,
    EarlyRedemptionFormControls,
    IUpdateCouponPaymentControls,
    IUpdateEarlyRedemptionControls,
    IUpdateProductControls,
    IUpdateUnderlyingInstrumentControls,
    MutualFundsDisabledFields,
    NonCurrencyFields,
    ProductActiveRequiredFields,
    ProductCategoryAndGroupAndSubTypes,
    ProductDateFields,
    ProductInSubscriptionRequiredFields,
    UnderlyingInstrumentFormControls,
    UpdateProductFormControls
} from '../../../../../assets/descriptors';
import { BaseSubProductFrom } from './base-sub-product.form';
import { BaseProductForm } from './base-product.form';
import { OnlyDigitsValidator, RequiredValidatorKey } from '@spa/validators';
import { MinimumNumberValidatorKey } from 'appEntries/panel/validators';
import * as moment from 'moment';
import { AlertsService } from 'appShared/services';
import { environment } from 'appEnv/environment';
import { AlertTypesEnum } from 'appShared/enums';
import {PanelSettingsProductsTrait} from "appEntries/panel/traits";

export class UpdateProductForm extends BaseProductForm<IUpdateProductControls> {
    public subscriptions: Subscription[];
    public documentFile: File;
    public documentErrorsSubject: Subject<string[]>;
    public earlyRedemptionForms: UpdateEarlyRedemptionForm[];
    public couponPaymentForms: UpdateCouponPaymentForm[];
    public underlyingInstrumentForms: UpdateUnderlyingInstrumentForm[];

    constructor(
        private readonly alertsService: AlertsService,
        private readonly fundamentalCompanyService: FundamentalCompanyService) {
        super();

        this.documentErrorsSubject = new Subject();

        this.subscriptions = [];
        this.earlyRedemptionForms = [];
        this.couponPaymentForms = [];
        this.underlyingInstrumentForms = [];
    }

    private validateLifetime(): boolean {
        const inputType = this.form.get(ProductFieldNames.Type);

        if (inputType.value === FundProductTypesEnum.MutualFund) {
            return true;
        }

        const compareGreaterThanToday = (name: string): boolean => {
            const bigInput = this.form.get(name);

            if (!bigInput.value) {
                return true;
            }

            return UpdateProductForm.getDateOrNull(bigInput.value) > moment(Date.now()).toDate();
        };

        const compareGreaterOrEqual = (big: string, small: string): boolean => {
            const bigInput = this.form.get(big);
            const smallInput = this.form.get(small);

            if (!smallInput.value || !bigInput.value) {
                return true;
            }

            return UpdateProductForm.getDateOrNull(bigInput.value) >= UpdateProductForm.getDateOrNull(smallInput.value);
        };

        const compareGreater = (big: string, small: string): boolean => {
            const bigInput = this.form.get(big);
            const smallInput = this.form.get(small);

            if (!smallInput.value || !bigInput.value) {
                return true;
            }

            return UpdateProductForm.getDateOrNull(bigInput.value) > UpdateProductForm.getDateOrNull(smallInput.value);
        };

        let lifetimeValid = true;

        if (!compareGreater(ProductFieldNames.SubscriptionPeriodEndsOn, ProductFieldNames.SubscriptionPeriodStartsOn)) {
            this.alertsService.addAlert(
                UpdateProductForm.messageGreater(this.controls.subscriptionPeriodEndsOn, this.controls.subscriptionPeriodStartsOn));
            lifetimeValid = false;
        }

        if (!compareGreaterOrEqual(ProductFieldNames.FirstExchangeTradingDate, ProductFieldNames.InitialFixingDate)) {
            this.alertsService.addAlert(
                UpdateProductForm.messageGreaterOrEqual(this.controls.firstExchangeTradingDate, this.controls.initialFixingDate));
            lifetimeValid = false;
        }

        if (!compareGreaterOrEqual(ProductFieldNames.FirstExchangeTradingDate, ProductFieldNames.SubscriptionPeriodEndsOn)) {
            this.alertsService.addAlert(
                UpdateProductForm.messageGreaterOrEqual(this.controls.firstExchangeTradingDate, this.controls.subscriptionPeriodEndsOn));
            lifetimeValid = false;
        }

        if (!compareGreaterOrEqual(ProductFieldNames.InitialFixingDate, ProductFieldNames.SubscriptionPeriodEndsOn)) {
            this.alertsService.addAlert(
                UpdateProductForm.messageGreaterOrEqual(this.controls.initialFixingDate, this.controls.subscriptionPeriodEndsOn));
            lifetimeValid = false;
        }

        if (!compareGreaterOrEqual(ProductFieldNames.LastExchangeTradingDate, ProductFieldNames.FinalFixingDate)) {
            this.alertsService.addAlert(
                UpdateProductForm.messageGreaterOrEqual(this.controls.lastExchangeTradingDate, this.controls.finalFixingDate));
            lifetimeValid = false;
        }

        if (!compareGreater(ProductFieldNames.ExpiresOn, ProductFieldNames.FinalFixingDate)) {
            this.alertsService.addAlert(
                UpdateProductForm.messageGreater(this.controls.expiresOn, this.controls.finalFixingDate));
            lifetimeValid = false;
        }

        if (!compareGreaterOrEqual(ProductFieldNames.ExpiresOn, ProductFieldNames.LastExchangeTradingDate)) {
            this.alertsService.addAlert(
                UpdateProductForm.messageGreaterOrEqual(this.controls.expiresOn, this.controls.lastExchangeTradingDate));
            lifetimeValid = false;
        }

        if (!compareGreaterOrEqual(ProductFieldNames.Redemption, ProductFieldNames.ExpiresOn)) {
            this.alertsService.addAlert(
                UpdateProductForm.messageGreaterOrEqual(this.controls.redemption, this.controls.expiresOn));
            lifetimeValid = false;
        }

        if (!compareGreaterThanToday(ProductFieldNames.ExpiresOn)) {
            this.alertsService.addAlert(`${this.controls.expiresOn.placeholder} should be greater than today.`);
            lifetimeValid = false;
        }

        if (inputType.value === FundProductTypesEnum.InvestmentCertificate) {
            if (!compareGreaterOrEqual(ProductFieldNames.IssuedOn, ProductFieldNames.SubscriptionPeriodEndsOn)) {
                this.alertsService.addAlert(
                    UpdateProductForm.messageGreaterOrEqual(this.controls.issuedOn, this.controls.subscriptionPeriodEndsOn));
                lifetimeValid = false;
            }

            // Disabled until Date Comparison methods are fixed
            // if (!compareGreaterOrEqual(ProductFieldNames.IssuedOn, ProductFieldNames.InitialFixingDate)) {
            //     this.alertsService.addAlert(
            //         UpdateProductForm.messageGreaterOrEqual(this.controls.issuedOn, this.controls.initialFixingDate));
            //     lifetimeValid = false;
            // }

            if (!compareGreaterOrEqual(ProductFieldNames.IssuedOn, ProductFieldNames.FirstExchangeTradingDate)) {
                this.alertsService.addAlert(
                    UpdateProductForm.messageGreaterOrEqual(this.controls.issuedOn, this.controls.firstExchangeTradingDate));
                lifetimeValid = false;
            }

            if (!compareGreaterOrEqual(ProductFieldNames.FinalFixingDate, ProductFieldNames.IssuedOn)) {
                this.alertsService.addAlert(UpdateProductForm.messageGreaterOrEqual(this.controls.finalFixingDate, this.controls.issuedOn));
                lifetimeValid = false;
            }

            if (!compareGreater(ProductFieldNames.ExpiresOn, ProductFieldNames.IssuedOn)) {
                this.alertsService.addAlert(
                    UpdateProductForm.messageGreater(this.controls.expiresOn, this.controls.issuedOn));
                lifetimeValid = false;
            }
        }
        
        return lifetimeValid;
    }

    private updateFormValue(product: FundProductInterface, key: string): void {
        const isCertificateOrBond =  PanelSettingsProductsTrait.isCertificateOrBond(product.type);
        const isCertificate = PanelSettingsProductsTrait.isCertificate(product.type);
        const isInSubscription = isCertificateOrBond && product.status === FundProductStatusesEnum.InSubscription;
        const isActive = isCertificate && product.status === FundProductStatusesEnum.Active;

        this.disableOrEnable(product, key);
        this.updateControlValue(product, key);

        if (isInSubscription && ProductInSubscriptionRequiredFields.includes(key)) {
            this.form.controls[key].setValidators(Validators.required);
        }
        else if (isActive && ProductActiveRequiredFields.includes(key)) {
            this.form.controls[key].setValidators(Validators.required);
        }
    }

    private updateControlValue(product: FundProductInterface, key: string) {
        const control = this.controls[key] as BaseControl;
        const formControl = this.form.controls[key];

        if (product.hasOwnProperty(key)) {
            if (control.hasOwnProperty('currency')) {
                const amountControl = control as AmountControl;
                const decimalPoints = amountControl.fourDecimals ? 4 : 2;

                if (NonCurrencyFields.indexOf(control.name) === -1 && !amountControl.isPercentage) {
                    amountControl.currency = product.currency;
                }

                if (!product[key] && product[key] !== 0) {
                    control.value = null;
                }
                else {
                    control.value = ReportExtensions.toDecimalString(product[key], decimalPoints, decimalPoints, false);
                }
            }
            else if (ProductDateFields.includes(key)) {
                if (!product[key]) {
                    control.value = null;
                }
                else {
                    control.value = moment(product[key]).format(environment.dateFormat);
                }
            }
            else {
                control.value = product[key];
            }

            formControl.setValue(control.value);
        }
        else {
            control.value = null;
            formControl.setValue(null);
        }
    }

    private disableOrEnable(
        product: FundProductInterface,
        key: string) {
        const control = this.controls[key] as BaseControl;
        const formControl = this.form.controls[key];

        if (product.type === FundProductTypesEnum.MutualFund && MutualFundsDisabledFields.indexOf(key) >= 0) {
            formControl.disable();
            control.disabled = true;
        }
        else {
            formControl.enable();
            control.disabled = false;
        }
    }

    protected customValidateForm(messageFunction: (controlsLabel?: string, controlLength?: number) => { [key: string]: string; }): boolean {
        const inputExchangeSymbol = this.form.get(ProductFieldNames.ExchangeSymbol);
        const inputListingMarket = this.form.get(ProductFieldNames.ListingMarket);
        const inputType = this.form.get(ProductFieldNames.Type);
        const inputSubType = this.form.get(ProductFieldNames.SubType);
        const inputInitialLeverage = this.form.get(ProductFieldNames.InitialLeverage);

        let hasError = false;

        if (inputExchangeSymbol.value && !inputListingMarket.value) {
            this.controls.listingMarket.setError(messageFunction(this.controls.listingMarket.placeholder)[RequiredValidatorKey]);
            hasError = true;
        }
        else if (!inputExchangeSymbol.value && inputListingMarket.value) {
            this.controls.exchangeSymbol.setError(messageFunction(this.controls.exchangeSymbol.placeholder)[RequiredValidatorKey]);
            hasError = true;
        }

        const initialLeverage = UpdateProductForm.getNumberOrZero(inputInitialLeverage.value);
        const hasInitialLeverageError = PanelSettingsProductsTrait.isCertificate(inputType.value)
            && inputSubType.value === ProductSubType.Warrant
            && initialLeverage <= 0;

        if (hasInitialLeverageError) {
            this.controls.initialLeverage.setError(
                messageFunction(this.controls.initialLeverage.placeholder, 0.01)[MinimumNumberValidatorKey]);
            hasError = true;
        }

        return this.validateLifetime() && !hasError;
    }

    initForm(product?: FundProductInterface): void {
        this.form = new FormGroup({});
        this.controls = UpdateProductFormControls(product);

        Object.keys(this.controls)
            .forEach(key => {
                const control = this.controls[key] as BaseControl;
                this.form.addControl(key, new FormControl(control.value, control.validators));
            });

        product.earlyRedemptions.forEach(() => {
            this.addEarlyRedemptionForm();
        });

        product.underlyingInstruments.forEach(() => {
            this.addUnderlyingInstrumentForm();
        });

        product.couponPayments.forEach(() => {
            this.addCouponPaymentForm();
        });

        if (product) {
            this.updateFormValues(product);
        }
    }

    updateFormValues(product: FundProductInterface): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());

        Object.keys(this.controls).forEach(key => {
            this.updateFormValue(product, key);
        });

        this.controls.category.options.forEach(option => option.disabled = false);
        this.controls.group.options.forEach(option => option.disabled = false);
        this.controls.subType.options.forEach(option => option.disabled = false);

        product.earlyRedemptions.forEach((item, index) => {
            this.earlyRedemptionForms[index].updateFormValues(item);
        });

        product.couponPayments.forEach((item, index) => {
            this.couponPaymentForms[index].updateFormValues(item);
        });

        product.underlyingInstruments.forEach((item, index) => {
            this.underlyingInstrumentForms[index].updateFormValues(item);
        });

        const formControlCategory = this.form.controls[ProductFieldNames.Category];
        const formControlSubType = this.form.controls[ProductFieldNames.SubType];
        const formControlGroup = this.form.controls[ProductFieldNames.Group];
        const formControlExpiresOn = this.form.controls[ProductFieldNames.ExpiresOn];
        const formControlOpenEnd = this.form.controls[ProductFieldNames.OpenEnd];
        const formControlConditionalCoupon = this.form.controls[ProductFieldNames.ConditionalCoupon];

        this.subscriptions.push(formControlCategory.valueChanges.subscribe(category => {
            this.onCategoryChange(category, product);
        }));

        this.subscriptions.push(formControlSubType.valueChanges.subscribe(subType => {
            this.onSubTypeChange(subType, product);
        }));

        this.subscriptions.push(formControlGroup.valueChanges.subscribe(group => {
            this.onGroupChange(group, product);
        }));

        this.subscriptions.push(formControlExpiresOn.valueChanges.subscribe(maturity => {
            this.onMaturityChange(maturity);
        }));

        this.onMaturityChange(product.expiresOn);

        this.subscriptions.push(formControlOpenEnd.valueChanges.subscribe(openEnd => {
            this.onOpenEndChange(openEnd);
        }));

        this.subscriptions.push(formControlConditionalCoupon.valueChanges.subscribe(conditionalCoupon => {
            this.onConditionalCouponChange(conditionalCoupon);
        }));

        this.onConditionalCouponChange(product.conditionalCoupon);
    }

    addEarlyRedemptionForm(): void {
        const form = new UpdateEarlyRedemptionForm(this, this.alertsService);
        form.initForm();
        this.earlyRedemptionForms.push(form);
    }

    addCouponPaymentForm(): void {
        const form = new UpdateCouponPaymentForm(this, this.alertsService);
        form.initForm();
        this.couponPaymentForms.push(form);
    }

    addUnderlyingInstrumentForm(): void {
        const form = new UpdateUnderlyingInstrumentForm(this, this.alertsService, this.fundamentalCompanyService);
        form.initForm();
        this.underlyingInstrumentForms.push(form);
    }

    removeEarlyRedemption(index: number): void {
        this.earlyRedemptionForms.splice(index, 1);
    }

    removeUnderlyingInstrument(index: number): void {
        this.underlyingInstrumentForms.splice(index, 1);
    }

    removeCouponPayment(index: number): void {
        this.couponPaymentForms.splice(index, 1);
    }

    onOpenEndChange(openEnd: boolean): void {
        const formControlDurationInMonths = this.form.controls[ProductFieldNames.DurationInMonths];
        const formControlExpiresOn = this.form.controls[ProductFieldNames.ExpiresOn];

        if (openEnd) {
            formControlDurationInMonths.setValue(null);
            formControlDurationInMonths.clearValidators();
            formControlDurationInMonths.disable();
            this.controls.durationInMonths.disabled = true;
            formControlDurationInMonths.updateValueAndValidity();

            formControlExpiresOn.setValue(null);
            formControlExpiresOn.clearValidators();
            formControlExpiresOn.disable();
            this.controls.expiresOn.disabled = true;
            formControlExpiresOn.updateValueAndValidity();
        }
        else {
            formControlDurationInMonths.setValidators([OnlyDigitsValidator(), Validators.maxLength(DurationInMonthsMaxLength)]);
            formControlDurationInMonths.enable();
            this.controls.durationInMonths.disabled = false;

            formControlExpiresOn.enable();
            this.controls.expiresOn.disabled = false;
        }
    }

    onConditionalCouponChange(conditionalCoupon: boolean): void {
        const formControlCouponTriggerLevel = this.form.controls[ProductFieldNames.CouponTriggerLevel];

        if (conditionalCoupon) {
            formControlCouponTriggerLevel.setValidators([Validators.required]);
            formControlCouponTriggerLevel.enable();
            this.controls.couponTriggerLevel.disabled = false;
        }
        else {
            formControlCouponTriggerLevel.setValue(null);
            formControlCouponTriggerLevel.clearValidators();
            formControlCouponTriggerLevel.disable();
            this.controls.couponTriggerLevel.disabled = true;
            formControlCouponTriggerLevel.updateValueAndValidity();
        }
    }

    onListingMarketChange(listingMarket: string): void {
        const formControl = this.form.controls[ProductFieldNames.ExchangeSymbol];

        if (listingMarket) {
            formControl.setValidators(Validators.required);
        }
        else {
            formControl.clearValidators();
        }

        formControl.updateValueAndValidity();
    }

    onSubTypeChange(subType: ProductSubType, product: FundProductInterface): void {
        if (!PanelSettingsProductsTrait.isProductCertificate(product)) {
            return;
        }

        const options = { onlySelf: true, emitEvent: false };

        if (subType === ProductSubType.NotAvailable) {
            return;
        }

        const groupAndCategory = ProductCategoryAndGroupAndSubTypes.find(item => item.subType === subType);

        if (groupAndCategory) {
            const formControlCategory = this.form.controls[ProductFieldNames.Category];
            const formControlGroup = this.form.controls[ProductFieldNames.Group];

            formControlCategory.setValue(groupAndCategory.category, options);
            formControlGroup.setValue(groupAndCategory.group, options);

            formControlCategory.updateValueAndValidity(options);
            formControlGroup.updateValueAndValidity(options);
        }
    }

    onGroupChange(group: ProductGroup, product: FundProductInterface): void {
        if (!PanelSettingsProductsTrait.isProductCertificate(product)) {
            return;
        }

        const options = { onlySelf: true, emitEvent: false };

        this.controls.category.options.forEach(option => option.disabled = false);
        this.controls.subType.options.forEach(option => option.disabled = false);

        if (group === ProductGroup.NotAvailable) {
            return;
        }

        const formControlCategory = this.form.controls[ProductFieldNames.Category];
        const formControlSubType = this.form.controls[ProductFieldNames.SubType];

        formControlCategory.setValue(ProductCategory.NotAvailable, options);
        formControlSubType.setValue(ProductSubType.NotAvailable, options);

        formControlCategory.updateValueAndValidity(options);
        formControlSubType.updateValueAndValidity(options);

        const maps = ProductCategoryAndGroupAndSubTypes.filter(item => item.group === group);

        this.controls.category.options
            .filter(option => (option.value as number) > 0)
            .forEach(option => {
                option.disabled = false;

                const found = maps.find(item => item.category === option.value);

                if (!found) {
                    option.disabled = true;
                }
            });

        this.controls.subType.options
            .filter(option => (option.value as number) > 0)
            .forEach(option => {
                option.disabled = false;

                const found = maps.find(item => item.subType === option.value);

                if (!found) {
                    option.disabled = true;
                }
            });
    }

    onCategoryChange(category: ProductCategory, product: FundProductInterface): void {
        if (!PanelSettingsProductsTrait.isProductCertificate(product)) {
            return;
        }

        const options = { onlySelf: true, emitEvent: false };

        this.controls.category.options.forEach(option => option.disabled = false);
        this.controls.subType.options.forEach(option => option.disabled = false);

        if (category === ProductCategory.NotAvailable) {
            return;
        }

        const formControlGroup = this.form.controls[ProductFieldNames.Group];
        const formControlSubType = this.form.controls[ProductFieldNames.SubType];

        formControlGroup.setValue(ProductGroup.NotAvailable, options);
        formControlSubType.setValue(ProductSubType.NotAvailable, options);

        formControlGroup.updateValueAndValidity(options);
        formControlSubType.updateValueAndValidity(options);

        const maps = ProductCategoryAndGroupAndSubTypes.filter(item => item.category === category);

        this.controls.group.options
            .filter(option => (option.value as number) > 0)
            .forEach(option => {
                option.disabled = false;

                const found = maps.find(item => item.group === option.value);

                if (!found) {
                    option.disabled = true;
                }
            });

        this.controls.subType.options
            .filter(option => (option.value as number) > 0)
            .forEach(option => {
                option.disabled = false;

                const found = maps.find(item => item.subType === option.value);

                if (!found) {
                    option.disabled = true;
                }
            });
    }

    onMaturityChange(maturity: Date): void {
        const formControlConditionalCoupon = this.form.controls[ProductFieldNames.ConditionalCoupon];

        if (maturity) {
            formControlConditionalCoupon.setValue(false);
            formControlConditionalCoupon.updateValueAndValidity();
        }
    }

    static getDateOrNull(value: any): (Date | null) {
        if (!value) {
            return null;
        }

        const parsed = moment(value).toDate();

        parsed.setHours(12, 0, 0);

        return parsed;
    }

    static getNumberOrZero(value: any): number {
        if (!value) {
            return 0;
        }

        return +Helper.compactString(value.toString());
    }

    static getNumberOrNull(value: any): (number | null) {
        if (!value) {
            return null;
        }

        return +Helper.compactString(value.toString());
    }

    static messageGreaterOrEqual(big: BaseControl, small: BaseControl): string {
        return `${big.placeholder} should be greater equal to ${small.placeholder}.`;
    }

    static messageGreater(big: BaseControl, small: BaseControl, context?: string): string {
        return `${big.placeholder} should be greater than ${small.placeholder}. ${context}`;
    }
}

export class UpdateEarlyRedemptionForm extends BaseSubProductFrom<IUpdateEarlyRedemptionControls, IEarlyRedemption> {
    constructor(private readonly parentForm: UpdateProductForm, private readonly alertsService: AlertsService) {
        super(
            [ProductRedemptionFieldNames.Amount, ProductRedemptionFieldNames.TriggerLevel],
            [ProductRedemptionFieldNames.ObservationDate, ProductRedemptionFieldNames.Date]);
    }
    protected postUpdateFormValues(entity: IEarlyRedemption): void {
    }

    protected postInitForm(): void {

    }

    protected customValidateForm(): boolean {
        const compareGreater = (bigInput: AbstractControl, smallInput: AbstractControl): boolean => {
            if (!smallInput.value || !bigInput.value) {
                return true;
            }

            return UpdateProductForm.getDateOrNull(bigInput.value) > UpdateProductForm.getDateOrNull(smallInput.value);
        };

        const context = '(Early Redemptions)';
        const parent = this.parentForm.form;

        let lifetimeValid = true;

        if (!compareGreater(parent.get(ProductFieldNames.ExpiresOn), this.form.get(ProductRedemptionFieldNames.ObservationDate))) {
            this.alertsService.addAlert(
                UpdateProductForm.messageGreater(this.parentForm.controls.expiresOn, this.controls.observationDate, context));
            lifetimeValid = false;
        }

        if (!compareGreater(this.form.get(ProductRedemptionFieldNames.ObservationDate), parent.get(ProductFieldNames.IssuedOn))) {
            this.alertsService.addAlert(
                UpdateProductForm.messageGreater(this.controls.observationDate, this.parentForm.controls.issuedOn, context));
            lifetimeValid = false;
        }

        if (!compareGreater(parent.get(ProductFieldNames.ExpiresOn), this.form.get(ProductRedemptionFieldNames.Date))) {
            this.alertsService.addAlert(
                UpdateProductForm.messageGreater(this.parentForm.controls.expiresOn, this.controls.date, context));
            lifetimeValid = false;
        }

        if (!compareGreater(this.form.get(ProductRedemptionFieldNames.Date), parent.get(ProductFieldNames.IssuedOn))) {
            this.alertsService.addAlert(
                UpdateProductForm.messageGreater(this.controls.date, this.parentForm.controls.issuedOn, context));
            lifetimeValid = false;
        }

        return lifetimeValid;
    }

    protected getFormControls(): IUpdateEarlyRedemptionControls {
        return EarlyRedemptionFormControls();
    }
}

export class UpdateCouponPaymentForm extends BaseSubProductFrom<IUpdateCouponPaymentControls, ICouponPayment> {
    constructor(private readonly parentForm: UpdateProductForm, private readonly alertsService: AlertsService) {
        super(
            [ProductCouponFieldNames.Amount],
            [ProductCouponFieldNames.ObservationDate, ProductCouponFieldNames.PaymentDate]);
    }

    protected postUpdateFormValues(entity: ICouponPayment): void {
    }

    protected postInitForm(): void {
        this.controls.amount.currency = this.parentForm.form.get(ProductFieldNames.Currency).value;
    }

    protected customValidateForm(): boolean {
        const compareGreater = (bigInput: AbstractControl, smallInput: AbstractControl): boolean => {
            if (!smallInput.value || !bigInput.value) {
                return true;
            }

            return UpdateProductForm.getDateOrNull(bigInput.value) > UpdateProductForm.getDateOrNull(smallInput.value);
        };

        const compareGreaterOrEqual = (bigInput: AbstractControl, smallInput: AbstractControl): boolean => {
            if (!smallInput.value || !bigInput.value) {
                return true;
            }

            return UpdateProductForm.getDateOrNull(bigInput.value) >= UpdateProductForm.getDateOrNull(smallInput.value);
        };

        const context = '(Coupon Payments)';
        const parent = this.parentForm.form;

        let lifetimeValid = true;

        if (!compareGreaterOrEqual(parent.get(ProductFieldNames.ExpiresOn), this.form.get(ProductCouponFieldNames.ObservationDate))) {
            this.alertsService.addAlert(
                UpdateProductForm.messageGreater(this.parentForm.controls.expiresOn, this.controls.observationDate, context));
            lifetimeValid = false;
        }

        if (!compareGreater(this.form.get(ProductCouponFieldNames.ObservationDate), parent.get(ProductFieldNames.IssuedOn))) {
            this.alertsService.addAlert(
                UpdateProductForm.messageGreater(this.controls.observationDate, this.parentForm.controls.issuedOn, context));
            lifetimeValid = false;
        }

        if (!compareGreaterOrEqual(parent.get(ProductFieldNames.ExpiresOn), this.form.get(ProductCouponFieldNames.PaymentDate))) {
            this.alertsService.addAlert(
                UpdateProductForm.messageGreater(this.parentForm.controls.expiresOn, this.controls.paymentDate, context));
            lifetimeValid = false;
        }

        if (!compareGreater(this.form.get(ProductCouponFieldNames.PaymentDate), parent.get(ProductFieldNames.IssuedOn))) {
            this.alertsService.addAlert(
                UpdateProductForm.messageGreater(this.controls.paymentDate, this.parentForm.controls.issuedOn, context));
            lifetimeValid = false;
        }

        if (!compareGreater(this.form.get(ProductCouponFieldNames.PaymentDate), this.form.get(ProductCouponFieldNames.ObservationDate))) {
            this.alertsService.addAlert(
                UpdateProductForm.messageGreater(this.controls.paymentDate, this.controls.observationDate, context));
            lifetimeValid = false;
        }

        const couponRate = UpdateProductForm.getNumberOrZero(this.parentForm.form.get(ProductFieldNames.AnnualInterestRate).value);

        if (couponRate > 0 && UpdateProductForm.getDateOrNull(this.form.get(ProductCouponFieldNames.PaymentDate).value) === null) {
            this.alertsService.addAlert('Payment date is required when Coupon Rate is filled. (Coupon Payments)');
            lifetimeValid = false;
        }

        const isConditional = !!this.parentForm.form.get(ProductFieldNames.ConditionalCoupon).value;

        if (isConditional && UpdateProductForm.getDateOrNull(this.form.get(ProductCouponFieldNames.ObservationDate).value) === null) {
            this.alertsService.addAlert('Observation date is required when Conditional Coupon is checked. (Coupon Payments)');
            lifetimeValid = false;
        }

        return lifetimeValid;
    }

    protected getFormControls(): IUpdateCouponPaymentControls {
        return CouponPaymentFormControls();
    }
}

export class UpdateUnderlyingInstrumentForm extends BaseSubProductFrom<IUpdateUnderlyingInstrumentControls, IUnderlyingInstrument> {

    private company?: IFundamentalCompany;
    private companies: IFundamentalCompany[];
    private valueChangesBound: boolean;

    constructor(
        private readonly parentForm: UpdateProductForm,
        private readonly alertsService: AlertsService,
        private readonly fundamentalCompanyService: FundamentalCompanyService) {
        super(
            [ProductInstrumentFieldNames.InitialFixingLevel, ProductInstrumentFieldNames.Price, ProductInstrumentFieldNames.Weight]);

        this.companies = [];
    }

    private bindValueChanges() {
        if (this.valueChangesBound) {
            return;
        }

        const subscription = this.form.controls[ProductInstrumentFieldNames.Symbol].valueChanges.subscribe((value: string) => {
            if (this.companies && value) {
                const symbol = value.toUpperCase();
                this.company = this.companies.find(company => company.symbol === symbol);
            }

            const formControlName = this.form.controls[ProductInstrumentFieldNames.Name];

            if (this.company) {
                formControlName.setValue(this.company.companyName);
            }
            else {
                formControlName.setValue(null);
            }

            formControlName.updateValueAndValidity();
        });

        this.parentForm.subscriptions.push(subscription);
        this.valueChangesBound = true;
    }

    protected postUpdateFormValues(entity: IUnderlyingInstrument): void {
        this.controls.symbol.setOptions([]);

        if (entity.symbol) {
            const subscription = this.fundamentalCompanyService
                .getCompanies([entity.symbol])
                .subscribe(response => {
                    if (response.status === HttpStatusCodesEnum.Ok) {
                        this.companies = response.data as IFundamentalCompany[];

                        if (!this.companies || this.companies.length === 0) {
                            this.controls.symbol.options.push({
                                label: `${entity.symbol} - The symbol is not available.`,
                                value: entity.symbol
                            });

                            this.company = null;
                            this.controls.symbol.value = entity.symbol;
                            this.form.controls[ProductInstrumentFieldNames.Symbol].setValue(entity.symbol);
                        }
                        else {
                            const options = this.companies.map(company => {
                                return {
                                    label: UpdateUnderlyingInstrumentForm.getCompanyLabel(company),
                                    value: company.symbol
                                };
                            });

                            this.company = this.companies.find(company => company.symbol === entity.symbol);
                            this.controls.symbol.setOptions(options);
                            this.controls.symbol.value = entity.symbol;
                            this.form.controls[ProductInstrumentFieldNames.Symbol].setValue(entity.symbol);
                        }
                    }
                });

            this.parentForm.subscriptions.push(subscription);
        }
    }

    protected postInitForm(): void {
        const seaechFields = ['symbol', 'companyName'];
        const projections = ['symbol', 'companyName'];

        this.controls.symbol.asyncOptions = (symbol: string) => {
            this.company = null;
            this.companies = [];

            if (!symbol) {
                return of([]);
            }

            return this.fundamentalCompanyService
                .search(symbol, seaechFields, projections, 1000)
                .pipe(map(response => {
                    if (response.status === HttpStatusCodesEnum.NotFound) {
                        this.alertsService.addAlert('No company found with this symbol.', AlertTypesEnum.Warning);

                        return [];
                    }
                    else if (response.status !== HttpStatusCodesEnum.Ok) {
                        this.alertsService.addAlert('Error while fetching companies.', AlertTypesEnum.Error);

                        return [];
                    }
                    else {
                        this.companies = response.data as IFundamentalCompany[];

                        if (!this.companies || this.companies.length === 0) {
                            return [{
                                label: `${symbol} - The symbol is not available.`,
                                value: symbol
                            }];
                        }

                        this.bindValueChanges();

                        return this.companies.map(company => {
                            return {
                                label: UpdateUnderlyingInstrumentForm.getCompanyLabel(company),
                                value: company.symbol
                            };
                        });
                    }
                }));
        };
    }

    protected customValidateForm(messageFunction: (controlsLabel?: string) => { [key: string]: string }): boolean {
        const priceInput = this.form.get(ProductInstrumentFieldNames.Price);
        const levelInput = this.form.get(ProductInstrumentFieldNames.InitialFixingLevel);
        const currencyInput = this.form.get(ProductInstrumentFieldNames.Currency);
        const levelControl = this.controls.initialFixingLevel;

        const priceInputValue = UpdateProductForm.getNumberOrNull(priceInput.value);
        const levelInputValue = UpdateProductForm.getNumberOrNull(levelInput.value);

        const hasCurrencyError = priceInputValue && !currencyInput.value;

        if (hasCurrencyError) {
            this.controls.currency.setError(messageFunction(this.controls.currency.placeholder)[RequiredValidatorKey]);
        }

        const hasInitialFixinfError = this.company && !levelInputValue;

        if (hasInitialFixinfError) {
            levelControl.setError(messageFunction(levelControl.placeholder)[RequiredValidatorKey]);
        }

        return !hasInitialFixinfError && !hasCurrencyError;
    }

    protected getFormControls(): IUpdateUnderlyingInstrumentControls {
        return UnderlyingInstrumentFormControls();
    }

    static getCompanyLabel(company: IFundamentalCompany): string {
        return company.companyName ? `${company.symbol} - ${company.companyName}` : company.symbol;
    }
}
